import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../lib/api';
import { Search, Trash2, Eye, ChevronLeft, ChevronRight, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

export default function AdminListings() {
  const [listings, setListings] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchListings = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 15 };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      const { data } = await adminAPI.getListings(params);
      setListings(data.listings); setTotal(data.total); setPages(data.pages);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchListings(); }, [page, statusFilter]);

  const handleRemove = async (listingId) => {
    if (!window.confirm('Remove this listing?')) return;
    try {
      await adminAPI.removeListing(listingId);
      toast.success('Listing removed');
      fetchListings();
    } catch (err) { toast.error('Failed to remove listing'); }
  };

  return (
    <div data-testid="admin-listings">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900">Listings Management</h1>
        <p className="text-sm text-zinc-500 mt-1">{total} total listings</p>
      </div>

      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <form onSubmit={e => { e.preventDefault(); setPage(1); fetchListings(); }} className="flex gap-2 flex-1">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search listings..."
              className="w-full pl-10 pr-4 py-2.5 border border-zinc-200 rounded-lg text-sm focus:ring-2 focus:ring-[#002FA7] bg-white"
              data-testid="admin-listings-search"
            />
          </div>
          <button type="submit" className="bg-[#002FA7] text-white px-4 py-2.5 rounded-lg text-sm font-bold">Search</button>
        </form>
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
          className="border border-zinc-200 rounded-lg px-3 py-2.5 text-sm bg-white"
          data-testid="admin-listings-status-filter"
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="deleted">Deleted</option>
          <option value="removed_by_admin">Removed by Admin</option>
        </select>
      </div>

      <div className="bg-white rounded-lg border border-zinc-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-listings-table">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr>
                <th className="text-left px-4 py-3 font-semibold text-zinc-600">Listing</th>
                <th className="text-left px-4 py-3 font-semibold text-zinc-600">Seller</th>
                <th className="text-left px-4 py-3 font-semibold text-zinc-600">Category</th>
                <th className="text-left px-4 py-3 font-semibold text-zinc-600">Price</th>
                <th className="text-left px-4 py-3 font-semibold text-zinc-600">City</th>
                <th className="text-left px-4 py-3 font-semibold text-zinc-600">Status</th>
                <th className="text-left px-4 py-3 font-semibold text-zinc-600">Views</th>
                <th className="text-right px-4 py-3 font-semibold text-zinc-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(5)].map((_, i) => <tr key={i} className="border-b border-zinc-100"><td colSpan={8} className="px-4 py-3"><div className="h-4 bg-zinc-100 rounded animate-pulse" /></td></tr>)
              ) : listings.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-zinc-400">No listings found</td></tr>
              ) : listings.map(l => (
                <tr key={l.listing_id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`listing-row-${l.listing_id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 rounded bg-zinc-100 overflow-hidden flex-shrink-0">
                        {l.images?.[0] ? <img src={l.images[0]} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-zinc-200" />}
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-zinc-900 truncate max-w-[200px]">{l.title}</p>
                        {l.promoted && <span className="inline-flex items-center text-[10px] text-[#002FA7] font-bold gap-0.5"><Zap className="w-3 h-3" />Promoted</span>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-zinc-600">{l.seller_name}</td>
                  <td className="px-4 py-3 text-zinc-600 capitalize">{l.category?.replace('_', ' ')}</td>
                  <td className="px-4 py-3 font-semibold text-zinc-900">${l.price?.toFixed(2)}</td>
                  <td className="px-4 py-3 text-zinc-600">{l.city}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${l.status === 'active' ? 'bg-emerald-100 text-emerald-700' : l.status === 'removed_by_admin' ? 'bg-red-100 text-red-700' : 'bg-zinc-100 text-zinc-600'}`}>
                      {l.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-zinc-500">{l.views}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <Link to={`/listing/${l.listing_id}`} className="p-1.5 rounded-md text-zinc-400 hover:text-[#002FA7] hover:bg-blue-50 transition-colors" data-testid={`view-listing-${l.listing_id}`}>
                        <Eye className="w-4 h-4" />
                      </Link>
                      {l.status === 'active' && (
                        <button onClick={() => handleRemove(l.listing_id)} className="p-1.5 rounded-md text-zinc-400 hover:text-red-600 hover:bg-red-50 transition-colors" data-testid={`remove-listing-${l.listing_id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-zinc-200 bg-zinc-50">
            <p className="text-xs text-zinc-500">Page {page} of {pages}</p>
            <div className="flex gap-1">
              <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} className="p-1.5 rounded-md border border-zinc-200 disabled:opacity-50"><ChevronLeft className="w-4 h-4" /></button>
              <button onClick={() => setPage(Math.min(pages, page + 1))} disabled={page === pages} className="p-1.5 rounded-md border border-zinc-200 disabled:opacity-50"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
